import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MilestoneCard from './MilestoneCard';
import { SPECIAL_MILESTONES } from '../data/loveData';

const MilestonesDisplay = ({ milestones, language, calculatedDays, anniversaryDate }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);
  
  // Always process milestones, with a default date if none provided
  const processedMilestones = milestones.map(milestone => {
    // Use today's date as the base if no anniversary date is selected
    const baseDate = anniversaryDate || new Date();
    const milestoneDate = new Date(baseDate);
    milestoneDate.setDate(milestoneDate.getDate() + (anniversaryDate ? milestone : 0));
    
    const isAchieved = anniversaryDate ? calculatedDays >= milestone : false;
    const daysToMilestone = anniversaryDate ? milestone - calculatedDays : milestone;
    
    return {
      days: milestone,
      date: milestoneDate,
      daysToMilestone,
      isAchieved,
      isSpecial: SPECIAL_MILESTONES.includes(milestone)
    };
  });
  
  // Filter milestones based on active filter
  const getFilteredMilestones = () => {
    let filtered = [];
    
    switch (activeFilter) {
      case 'upcoming':
        filtered = processedMilestones.filter(m => !m.isAchieved);
        break;
      case 'achieved':
        filtered = processedMilestones.filter(m => m.isAchieved);
        break;
      case 'special':
        filtered = processedMilestones.filter(m => m.isSpecial);
        break;
      default:
        filtered = processedMilestones;
    }
    
    return showAll ? filtered : filtered.slice(0, 8);
  };
  
  // Check if there are more items to show
  const hasMoreItems = processedMilestones.length > 8;
  const filteredMilestones = getFilteredMilestones();
  
  return (
    <div className="milestones-display">
      {/* Filter Tabs - Always show these */}
      <div className="flex justify-end mb-4">
        <div className="inline-flex bg-white dark:bg-gray-750 rounded-lg p-1 shadow-sm border border-gray-200 dark:border-gray-700">
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              activeFilter === 'all' ? 
              'bg-love-100 dark:bg-love-900 text-love-700 dark:text-love-300 font-medium' : 
              'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={() => setActiveFilter('all')}
          >
            {language === 'zh' ? '全部' : 'All'}
          </button>
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              activeFilter === 'upcoming' ? 
              'bg-love-100 dark:bg-love-900 text-love-700 dark:text-love-300 font-medium' : 
              'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={() => setActiveFilter('upcoming')}
          >
            {language === 'zh' ? '即將到來' : 'Upcoming'}
          </button>
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              activeFilter === 'achieved' ? 
              'bg-love-100 dark:bg-love-900 text-love-700 dark:text-love-300 font-medium' : 
              'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={() => setActiveFilter('achieved')}
          >
            {language === 'zh' ? '已達成' : 'Achieved'}
          </button>
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              activeFilter === 'special' ? 
              'bg-love-100 dark:bg-love-900 text-love-700 dark:text-love-300 font-medium' : 
              'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={() => setActiveFilter('special')}
          >
            {language === 'zh' ? '特殊紀念' : 'Special'}
          </button>
        </div>
      </div>

      {/* Always show milestone cards */}
      {filteredMilestones.length > 0 ? (
        <>
          {/* Milestone Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <AnimatePresence>
              {filteredMilestones.map((milestone, index) => (
                <MilestoneCard
                  key={milestone.days}
                  milestone={milestone.days}
                  date={milestone.date}
                  daysToMilestone={milestone.daysToMilestone}
                  isAchieved={milestone.isAchieved}
                  language={language}
                  index={index}
                />
              ))}
            </AnimatePresence>
          </div>
          
          {/* Show More/Less Button */}
          {hasMoreItems && (
            <div className="flex justify-center mt-4">
              <motion.button
                onClick={() => setShowAll(!showAll)}
                className="px-4 py-2 bg-white dark:bg-gray-750 border border-love-200 dark:border-love-800 rounded-full text-sm text-love-600 dark:text-love-400 hover:bg-love-50 dark:hover:bg-love-900/20 transition-colors shadow-sm"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {showAll ? 
                  (language === 'zh' ? '顯示較少' : 'Show Less') : 
                  (language === 'zh' ? '顯示更多' : 'Show More')}
              </motion.button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">
            {language === 'zh' ? '沒有符合條件的里程碑' : 'No milestones match your filter'}
          </p>
        </div>
      )}
      
      {/* Show a subtle message when no anniversary date is selected */}
      {!anniversaryDate && (
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">
            {language === 'zh' ? '選擇紀念日期以查看精確的里程碑日期' : 'Select an anniversary date to see exact milestone dates'}
          </p>
        </div>
      )}
    </div>
  );
};

export default MilestonesDisplay;